import translateLDSDestinations from "../services/translateLDSDestinations";

/**
 * Curated overrides — IATA is SSOT key. LDS autocomplete catalog fills gaps.
 * @type {Record<string, { labelHe?: string, cityEn?: string, countryEn?: string }>}
 */
export const DESTINATION_TRANSLATIONS = {
  HER: { labelHe: "כרתים", cityEn: "Crete" },
  CFU: { labelHe: "קורפו", cityEn: "Corfu" },
  CGN: { labelHe: "קלן", cityEn: "Cologne" },
  RHO: { labelHe: "רודוס", cityEn: "Rhodes" },
  ATH: { labelHe: "אתונה", cityEn: "Athens" },
  BCN: { labelHe: "ברצלונה", cityEn: "Barcelona" },
  TLV: { labelHe: "תל אביב", cityEn: "Tel Aviv" },
  DXB: { labelHe: "דובאי", cityEn: "Dubai" },
  JMK: { labelHe: "מיקונוס", cityEn: "Mykonos" },
  ALL: { labelHe: "כל היעדים", cityEn: "All Destinations" },
};

export function normalizeDestinationIata(code) {
  const normalized = String(code ?? "")
    .trim()
    .toUpperCase();
  return normalized || null;
}

function titleCaseEnglish(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  return raw
    .toLowerCase()
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

/**
 * Resolve destination display metadata by IATA (SSOT).
 */
export function lookupDestinationByIata(iataCode) {
  const iata = normalizeDestinationIata(iataCode);
  if (!iata) {
    return null;
  }

  const override = DESTINATION_TRANSLATIONS[iata];
  const catalog = translateLDSDestinations(iata);

  const cityEnRaw =
    override?.cityEn ||
    catalog?.CITYNAME ||
    catalog?.AIRPORTNAME ||
    iata;
  const cityEn = titleCaseEnglish(cityEnRaw);
  const countryEn = titleCaseEnglish(
    override?.countryEn || catalog?.COUNTRYNAME || "",
  );

  const catalogHeb = String(catalog?.CITYNAME_HEB || "").trim();
  const labelHe =
    override?.labelHe ||
    (catalogHeb && catalogHeb.toLowerCase() !== cityEnRaw.toLowerCase()
      ? catalogHeb
      : null) ||
    cityEn;

  const sublabelParts = [cityEn, iata].filter(Boolean);
  const sublabel =
    countryEn && !sublabelParts.includes(countryEn)
      ? `${cityEn} · ${iata}`
      : sublabelParts.join(" · ");

  return {
    iata,
    labelHe,
    cityEn,
    countryEn,
    sublabel,
  };
}

/**
 * Primary display: Hebrew label + IATA, e.g. "כרתים (HER)".
 * Fallback when no Hebrew: "Crete (HER)".
 */
export function formatDestinationDisplay(iataCode, cityNameFallback) {
  const iata = normalizeDestinationIata(iataCode);
  if (!iata) return String(cityNameFallback || "").trim() || "—";
  if (iata === "ALL") {
    return DESTINATION_TRANSLATIONS.ALL?.labelHe || "כל היעדים";
  }

  const override = DESTINATION_TRANSLATIONS[iata];
  const catalog = translateLDSDestinations(iata);
  const catalogHeb = String(catalog?.CITYNAME_HEB || "").trim();
  const hasCuratedOrHebrew = Boolean(override?.labelHe || catalogHeb);

  if (!hasCuratedOrHebrew && cityNameFallback) {
    return `${titleCaseEnglish(cityNameFallback)} (${iata})`;
  }

  const info = lookupDestinationByIata(iata);
  if (!info) {
    const city = titleCaseEnglish(cityNameFallback || iata);
    return `${city} (${iata})`;
  }

  const hasDistinctHebrew =
    info.labelHe &&
    info.labelHe.toLowerCase() !== info.cityEn.toLowerCase() &&
    info.labelHe.toUpperCase() !== iata;

  if (hasDistinctHebrew) {
    return `${info.labelHe} (${iata})`;
  }

  const city = info.cityEn || titleCaseEnglish(cityNameFallback) || iata;
  return `${city} (${iata})`;
}

export function formatDestinationSublabel(iataCode, cityNameFallback) {
  const iata = normalizeDestinationIata(iataCode);
  if (!iata || iata === "ALL") {
    return DESTINATION_TRANSLATIONS.ALL?.cityEn || "All Destinations";
  }
  const info = lookupDestinationByIata(iata);
  if (info?.sublabel) return info.sublabel;
  const city = titleCaseEnglish(cityNameFallback || iata);
  return `${city} · ${iata}`;
}

export function buildDestinationSearchText(iataCode, cityNameFallback, countryNameFallback) {
  const iata = normalizeDestinationIata(iataCode);
  const info = iata ? lookupDestinationByIata(iata) : null;
  const parts = [
    info?.labelHe,
    info?.cityEn,
    info?.countryEn,
    iata,
    cityNameFallback,
    countryNameFallback,
  ]
    .filter(Boolean)
    .map((part) => String(part).trim().toLowerCase());
  return [...new Set(parts)].join(" ");
}

/**
 * Display model for corporate destination picker (does not mutate LDS payload).
 */
export function resolveDestinationDisplayFromLocation(location) {
  if (!location || typeof location !== "object") {
    return {
      displayLabel: "",
      sublabel: "",
      searchText: "",
    };
  }

  const iata = normalizeDestinationIata(location.AIRPORTCODE || location.CITYCODE);
  if (iata === "ALL") {
    return {
      displayLabel: formatDestinationDisplay("ALL"),
      sublabel: formatDestinationSublabel("ALL"),
      searchText: buildDestinationSearchText("ALL"),
    };
  }

  const cityFallback = location.CITYNAME || location.CITYNAME_HEB || "";
  const countryFallback = location.COUNTRYNAME || location.COUNTRYNAME_HEB || "";

  return {
    displayLabel: formatDestinationDisplay(iata, cityFallback),
    sublabel: formatDestinationSublabel(iata, cityFallback),
    searchText: buildDestinationSearchText(iata, cityFallback, countryFallback),
  };
}

export function destinationMatchesSearchQuery(query, searchText) {
  const q = String(query ?? "").trim().toLowerCase();
  if (!q) return true;
  return String(searchText || "").toLowerCase().includes(q);
}
